import React, {useEffect, useState} from 'react';
import moment from "moment";
import {Box, Grid, Paper, Table, TableBody, TableContainer, Typography, Button, InputAdornment, TextField, TableHead, TableRow, TableCell, IconButton} from "@mui/material";
import DateRangePickerComponent from "../common/CustomDateRangePicker";
import DownloadIcon from '@mui/icons-material/Download';
import AnomalyRowData from './anomaly-row-data.component';
import BackButton from "../common/back-button";
import useAnomalyDetection from "../../hooks/useAnomalyDetection";
import AnomalyListSkeleton from "./skeleton/anomaly-list.skeleton";
import ClearIcon from "@mui/icons-material/Clear";
import useFilters from "../../hooks/useFilters";
import ReactSelect from "react-select";

const filterStyles = {
    control: (styles, { isFocused }) => ({
        ...styles,
        textAlign: "left",
        backgroundColor: "#fff",
        minHeight: 20, 
        padding: "0 8px", 
        height: 34, 
        width: 280,
        fontSize: 14,
        borderColor: "rgb(122, 120, 247)",
        '&:hover': {
            borderColor: "rgb(122, 120, 247)", 
        },
        zIndex: 1200,
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 1200,
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999, 
    }),
    valueContainer: (styles) => ({
        ...styles,
        padding: "0px 6px",
    }),
    input: (styles) => ({
        ...styles,
        margin: 0,
        padding: 0, 
    }),
    option: (styles) => ({...styles, textAlign: "left", fontSize: 13}),
    dropdownIndicator: (styles) => ({
        ...styles,
        color: "rgb(122, 120, 247)", 
        marginTop: "-2px",
        '&:hover': {
            color: "rgb(122, 120, 247)",
        },
    }),
    clearIndicator: (styles) => ({
        ...styles,
        color: "rgb(122, 120, 247)", 
        marginTop: "-2px",
        '&:hover': {
            color: "rgb(122, 120, 247)", 
        },
    }),
    singleValue: (styles) => ({
        ...styles,
        color: "rgba(51, 51, 207, 0.808)",
        marginTop: "0px",
        fontSize: 13,
    }),
    placeholder: (styles) => ({ ...styles, color: "#3333CFCE", marginTop: "0px", fontSize: 12}), 
    noOptionsMessage: (provided) => ({
        ...provided,
        fontStyle: "italic", 
        textAlign: "start", 
        fontSize: "13px", 
    }),
}

const anomalyTableHeaders = [
    {label: 'Hash Reference'},
    {label: 'Product Name'},
    {label: 'Last Location'},
    {label: 'Log Date'},
    {label: 'Fraud Type'},
    {label: 'Remarks'},
    {label: 'Action'}
]

const AnomalyListComponent = () => {

    const {isFetchingAnomalyList, anomalyList, searchedResult, isSearchingOVI, getAnomalyList, getSearchAnomalyList} = useAnomalyDetection();
    const {fraudTypeList, getDistinctFraudType} = useFilters();

    const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().endOf('month').format('YYYY-MM-DD'));
    const [searchedOVI, setSearchedOVI] = useState("");

    const [formattedFraudTypeList, setFormattedFraudTypeList] = useState([]);
    const [selectedFraud, setSelectedFraud] = useState(" ");
    const [selectedFraudValue, setSelectedFraudValue] = useState(" ");
    

    useEffect(() => {
        getDistinctFraudType();
    }, [])

    useEffect(() => {
        if (fraudTypeList) {
            const formattedList = fraudTypeList?.map(fraud => ({
                label: fraud.fraud_type,
                value: fraud.fraud_type
            }));

            setFormattedFraudTypeList(formattedList);
        }
    }, [fraudTypeList])
    

    useEffect(() => {
        getAnomalyList(startDate, endDate, selectedFraudValue);
    }, [startDate, endDate, selectedFraudValue])
   

    const handleDateRangeChange = (newStartDate, newEndDate) => {
        setStartDate(newStartDate);
        setEndDate(newEndDate);
    }

    const handleSearchOVI = (e) => {
        setSearchedOVI(e.target.value);

        if (e.key === "Enter") {
            getSearchAnomalyList(startDate, endDate, searchedOVI);
        }
    }

    const handleClearInput = () => {
        setSearchedOVI("");
        getAnomalyList(startDate, endDate, "");
    }

    const handleSelectFraudTypeFilter = (selectedOption) => {
        if (!selectedOption) {
            setSelectedFraud(" ");
            setSelectedFraudValue(" ");

        } else {
            setSelectedFraud(selectedOption);
            setSelectedFraudValue(selectedOption?.value);
        }
    }


    return (
        <Box>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h6" sx={{color: "#58565f", fontWeight: 600, fontSize: 20}}>
                        List of Anomalies
                    </Typography>
                </Grid>
            </Grid>

            <Box sx={{mt: 3}}>
              
                <Grid container>

                    <BackButton />
                
                    <Grid item xs={9} sx={{ml: "auto", display: 'flex', justifyContent: "flex-end" }}>
                        <ReactSelect
                            isRtl={false}
                            options={formattedFraudTypeList}
                            value={selectedFraud}
                            onChange={selectedOption => handleSelectFraudTypeFilter(selectedOption)}
                            styles={filterStyles}
                            isClearable={true}
                            placeholder={(selectedFraud != " ") ? null : "Select Fraud Type"}
                            noOptionsMessage={() => "No matching fraud type found."} 
                            menuPortalTarget={document.body} 
                            menuPosition="fixed"
                        />

                        &nbsp; &nbsp;

                        <TextField
                            sx={{
                                "& .MuiInputBase-root": { height: 33, border: "1px solid #7A78F7", color: "#3333CFCE", backgroundColor: '#fff', width: "auto", borderRadius: "5px" },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                            }}
                            variant="outlined"
                            autoComplete="off"
                            inputProps={{ style: { padding: "5px 12px 5px 0px", fontSize: 12, fontStyle: 'italic' }, border: "1px solid #7A78F7" }}
                            placeholder="Search Hash Reference"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <p style={{ fontWeight: 'bold', fontSize: 12, margin: 0, color: "#3333CFCE"}}>
                                            Hash Reference:
                                        </p>
                                    </InputAdornment>
                                ),
                                endAdornment: searchedOVI && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleClearInput(e)}
                                        >
                                            <ClearIcon sx={{color: "#3333CFCE", fontSize: 17}} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            value={searchedOVI}
                            onChange={(e) => setSearchedOVI(e.target.value)}
                            onKeyDown={handleSearchOVI}
                        />

                        &nbsp;&nbsp;&nbsp;

                        <DateRangePickerComponent
                            initialStartDate={startDate}
                            initialEndDate={endDate}
                            ranges={{
                                "Last 7 Days": [moment().subtract(6, 'days'), moment()],
                                "This Month": [moment().startOf('month'), moment().endOf('month')],
                                "Last Month": [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
                                "Last 90 Days": [moment().subtract(89, 'days'), moment()],
                            }}
                            onDateRangeChange={handleDateRangeChange}
                        />

                        &nbsp;&nbsp;&nbsp;

                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon sx={{height: 18}} />}
                            sx={{height: "33px", fontSize: 12, background: '#5959D8', color: '#fff', textTransform: 'none', "&:hover": {background: '#5959D8'}}}
                        >
                            Export as PDF
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TableContainer component={Paper} sx={{borderRadius: 2, width: "100%", mt: 2, maxHeight: 755, overflow: "auto",
                        "&::-webkit-scrollbar": {
                            width: "6px",
                        },
                        "&::-webkit-scrollbar-track": {
                            background: "rgba(217, 217, 217, 0.5)"
                        },
                        "&::-webkit-scrollbar-thumb": {
                            background: "#7a78f7a6",
                            borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                            background: "#7A78F7",
                            
                        }}}
                    >
                        <Table stickyHeader aria-label="sticky table" sx={{minWidth: 650}}>
                            <TableHead sx={{border: "1px solid #3333cf54"}}>
                                <TableRow>
                                    {anomalyTableHeaders?.map((header, index) => (
                                        <TableCell key={index} sx={{ fontWeight: 700, color: '#3333CFCE', backgroundColor: "#D8D8FD", borderTop: "1px solid #7A78F7",
                                            borderBottom: "1px solid #7A78F7", borderLeft: (index === 0) ? "1px solid #7A78F7" : {}, borderTopLeftRadius: (index === 0) ? "8px" : {},
                                            borderRight: (index === 6) ? "1px solid #7A78F7" : {}, borderTopRightRadius: (index === 6) ? "2px" : {}, 
                                            textAlign: (header?.label == "Last Location") ? "center" : "left" 
                                        }}>
                                            {header.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            {(isFetchingAnomalyList) ?
                                <TableBody sx={{maxHeight: "calc(100vh - 300px)", overflowY: "auto"}}>
                                    <AnomalyListSkeleton />
                                
                                </TableBody>
                            :

                                <TableBody sx={{maxHeight: "calc(100vh - 300px)", overflowY: "auto"}}>
                                    {(isSearchingOVI) ?
                                        ((searchedResult?.length > 0) ?
                                            searchedResult?.map((anomaly, index) => (
                                                <AnomalyRowData key={index} anomaly={anomaly} />
                                            )) 
                                        :
                                            <TableRow sx={{'&:last-child td, &:last-child th': {border: 0}, '& td, & th': {borderBottom: 0}}}>
                                                <TableCell colSpan={6} align="center">
                                                    <Typography sx={{fontSize: 16, color: "#58565f", padding: "40px 40px 20px 40px"}}>
                                                        No searched result(s)
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    :
                                        ((anomalyList?.length > 0) ?
                                            anomalyList?.map((anomaly, index) => (
                                                <AnomalyRowData key={index} anomaly={anomaly} />
                                            )) 
                                        :
                                            <TableRow sx={{'&:last-child td, &:last-child th': {border: 0}, '& td, & th': {borderBottom: 0}}}>
                                                <TableCell colSpan={6} align="center">
                                                    <Typography sx={{fontSize: 16, color: "#58565f", padding: "40px 40px 20px 40px"}}>
                                                        No available data to display
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    } 
                                    <br/>
                                </TableBody>
                            }
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    )
}

export default AnomalyListComponent